import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import './Admin.css';

function SystemStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load system stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    // Refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatMinutes = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  if (loading) {
    return <div className="loading">Loading stats...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
        <button onClick={loadStats} className="btn btn-secondary btn-sm" style={{ marginLeft: '1rem' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="system-stats">
      <div className="stats-header">
        <h3>System Resources</h3>
        <button className="btn btn-secondary btn-sm" onClick={loadStats}>
          Refresh
        </button>
      </div>

      <div className="stats-grid">
        {/* CPU */}
        <div className="stat-card card">
          <h4>CPU Usage</h4>
          <div className="stat-value">{stats.system.cpu_percent.toFixed(1)}%</div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{
                width: `${stats.system.cpu_percent}%`,
                backgroundColor: stats.system.cpu_percent > 80 ? 'var(--color-error)' : 'var(--color-accent)',
              }}
            />
          </div>
        </div>

        {/* Memory */}
        <div className="stat-card card">
          <h4>Memory Usage</h4>
          <div className="stat-value">{stats.system.memory.percent.toFixed(1)}%</div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{
                width: `${stats.system.memory.percent}%`,
                backgroundColor: stats.system.memory.percent > 80 ? 'var(--color-error)' : 'var(--color-accent)',
              }}
            />
          </div>
          <p className="stat-detail">
            {formatBytes(stats.system.memory.available)} available of {formatBytes(stats.system.memory.total)}
          </p>
        </div>

        {/* Disk */}
        <div className="stat-card card">
          <h4>Disk Usage</h4>
          <div className="stat-value">{stats.system.disk.percent.toFixed(1)}%</div>
          <div className="progress-bar">
            <div
              className="progress-bar-fill"
              style={{
                width: `${stats.system.disk.percent}%`,
                backgroundColor: stats.system.disk.percent > 80 ? 'var(--color-error)' : 'var(--color-accent)',
              }}
            />
          </div>
          <p className="stat-detail">
            {formatBytes(stats.system.disk.free)} free of {formatBytes(stats.system.disk.total)}
          </p>
        </div>
      </div>

      {/* User Stats */}
      <div className="stats-section">
        <h3>Users</h3>
        <div className="stats-row">
          <div className="stat-item">
            <span className="stat-label">Total Users</span>
            <span className="stat-number">{stats.users.total}</span>
          </div>
        </div>
      </div>

      {/* Transcript Stats */}
      <div className="stats-section">
        <h3>Transcripts</h3>
        <div className="stats-row stats-row-split">
          <div className="stats-group">
            <div className="stats-group-label">Current</div>
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-number">{stats.transcripts.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Pending</span>
                <span className="stat-number status-pending-text">{stats.transcripts.pending}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Processing</span>
                <span className="stat-number status-processing-text">{stats.transcripts.processing}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Completed</span>
                <span className="stat-number status-completed-text">{stats.transcripts.completed}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Failed</span>
                <span className="stat-number status-failed-text">{stats.transcripts.failed}</span>
              </div>
            </div>
          </div>
          {stats.all_time && (
            <div className="stats-group">
              <div className="stats-group-label">All-Time</div>
              <div className="stats-row">
                <div className="stat-item">
                  <span className="stat-label">Completed</span>
                  <span className="stat-number">{stats.all_time.total_transcriptions.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Audio Processed</span>
                  <span className="stat-number">{formatMinutes(stats.all_time.total_audio_minutes)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SystemStats;
